import { UseQueryOptions } from '@tanstack/react-query';
import { useDataChainId } from '@ui/hooks/datachain';
import { useQueryOnce } from '@ui/hooks/useQueryOnce';
import { toBigValue, toReadableValue } from '@ui/lib/bignumber';
import { PayablePoolToken } from '@ui/types/token';
import { Nullable } from '@ui/types/utility';
import BigNumber from 'bignumber.js';
import { isNil } from 'lodash-es';
import { useAccount, usePublicClient } from 'wagmi';

import { getTradingPortalFacet } from '../../contract-instances/getTradingPortalFacet';
import { Position } from '../TradingReaderFacet/useGetPositionV2/types';

export const useEstimateAddMarginGasLimit = <TSelect = BigNumber>({
  positionHash,
  amount,
  payToken,
  opts,
}: {
  positionHash: Nullable<Position['id']>;
  amount: Nullable<BigNumber>;
  payToken: PayablePoolToken;
  opts?: Pick<UseQueryOptions<BigNumber, Error, TSelect>, 'enabled'>;
}) => {
  const { address } = useAccount();
  const chainId = useDataChainId();
  const client = usePublicClient({
    chainId,
  });

  return useQueryOnce({
    meta: {
      type: 'estimateGas',
    },
    queryKey: [
      'TradingPortalFacet.estimateGas.addMargin',
      address,
      chainId,
      client?.uid,
      positionHash,
      amount,
      payToken.symbol,
    ],
    queryFn: async () => {
      if (
        isNil(positionHash) ||
        isNil(address) ||
        !client ||
        !amount ||
        !payToken
      ) {
        throw new Error('Invalid position hash or address, client');
      }
      const contract = getTradingPortalFacet({
        chainId,
        publicClient: client,
      });

      const amountIn = toBigValue(amount, payToken.decimals);

      const result = await contract.estimateGas.addMargin(
        [positionHash, payToken.address, amountIn],
        {
          account: address,
          value: payToken.isNative ? amountIn : undefined,
        },
      );

      if (isNil(result)) return undefined;

      return toReadableValue(result);
    },
    enabled:
      !!address &&
      !!positionHash &&
      !!amount &&
      amount.gt(0) &&
      !!payToken &&
      (opts?.enabled ?? true),
  });
};
